import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { Service } from 'app/models/services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';

@Component({
  selector: 'app-modifier-service',
  templateUrl: './modifier-service.component.html',
  styleUrls: ['./modifier-service.component.scss']
})
export class ModifierServiceComponent implements OnInit {
  // Service data
  service: Service = new Service();
  agenciesList: any[] = [];
  idService: string | null = null;
  
  // Images
  selectedFiles: FileList | null = null;
  isUploading: boolean = false;
  uploadType: 'main' | 'promo' | null = null;
  
  // Form state
  isSubmitting: boolean = false;
  
  // Category management
  categoryInput: string = '';
  
  // Prix
  prixInput: string = '';
  
  // Video URL
  videoUrl: string = '';
  
  // Image change flags
  isMainImageChanged: boolean = false;
  isPromoImageChanged: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private agenciesService: AgenciesService,
    private servicesService: ServicesService,
    private awsService: AwscontrolService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.loadAgencies();
    
    this.servicesService.refreshNedeed.subscribe(() => {
      this.loadServiceDetails();
    });

    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => {
        console.log('✅ AWS S3 Connection Healthy:', response);
      },
      error: (err) => {
        console.warn('⚠️ AWS S3 Connection Warning:', err);
      }
    });
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.servicesService.getService(this.idService).subscribe({
        next: (data: Service) => {
          this.service = data;
          
          // Initialize prix input
          if (this.service.prix === null || this.service.prix === undefined) {
            this.prixInput = '';
          } else {
            // Convert to string safely
            this.prixInput = String(this.service.prix);
          }
          
          // Initialize videoUrl
          this.videoUrl = this.service.videoUrl || '';
        },
        error: (error) => {
          console.error('Error loading service:', error);
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Impossible de charger les détails du service',
            confirmButtonColor: '#f44336'
          });
        }
      });
    }
  }

  loadAgencies(): void {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        this.agenciesList = data;
      },
      error: (error) => {
        console.error('Error loading agencies:', error);
      }
    });
  }

  // Format text methods
  formatText(command: string): void {
    if (!this.service.description) this.service.description = '';
    
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.service.description.substring(start, end);
    
    let formattedText = '';
    switch (command) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'paragraph':
        formattedText = `<p>${selectedText}</p>`;
        break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1':
        formattedText = `<h1>${selectedText}</h1>`;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText}</h2>`;
        break;
    }
    
    const newText = this.service.description.substring(0, start) + formattedText + this.service.description.substring(end);
    this.service.description = newText;
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // Category management
  addCategory(): void {
    if (this.categoryInput.trim()) {
      if (!this.service.categoryList) {
        this.service.categoryList = [];
      }
      this.service.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(index: number): void {
    if (this.service.categoryList) {
      this.service.categoryList.splice(index, 1);
    }
  }

  // Image handling
  onFileSelected(event: any, type: 'main' | 'promo'): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedFiles = files;
      this.uploadImage(type);
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'La taille maximale autorisée est de 5MB',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }

    return true;
  }

  uploadImage(type: 'main' | 'promo'): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;

    const file = this.selectedFiles[0];
    if (!this.validateImageFile(file)) return;

    this.isUploading = true;
    this.uploadType = type;

    const folder = type === 'main' ? 'service-images' : 'service-promo-images';
    
    this.awsService.uploadFile(file, folder).subscribe({
      next: (response) => {
        this.isUploading = false;
        this.uploadType = null;

        if (response.success && response.urls && response.urls.length > 0) {
          const uploadedUrl = response.urls[0];
          
          if (type === 'main') {
            this.service.imageUrl = uploadedUrl;
            this.isMainImageChanged = true;
          } else {
            this.service.promoImg = uploadedUrl;
            this.isPromoImageChanged = true;
          }

          Swal.fire({
            icon: 'success',
            title: 'Image téléchargée',
            text: `L'image ${type === 'main' ? 'principale' : 'promotionnelle'} a été téléchargée avec succès`,
            confirmButtonColor: '#4caf50',
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadType = null;
        
        console.error('Upload error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Erreur de téléchargement',
          text: 'Impossible de télécharger l\'image',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  removeImage(type: 'main' | 'promo'): void {
    Swal.fire({
      title: 'Supprimer l\'image?',
      text: `Voulez-vous vraiment supprimer l'image ${type === 'main' ? 'principale' : 'promotionnelle'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        if (type === 'main') {
          this.service.imageUrl = '';
          this.isMainImageChanged = true;
        } else {
          this.service.promoImg = '';
          this.isPromoImageChanged = true;
        }
      }
    });
  }

  // Form submission (with videoUrl)
  updateService(): void {
    this.isSubmitting = true;

    // Convert prixInput
    let prixValue: any = '';
    if (this.prixInput && this.prixInput.trim() !== '') {
      const prixLower = this.prixInput.toLowerCase().trim();
      
      if (prixLower === 'à contacter' || prixLower === 'a contacter' || prixLower === 'contactez-nous') {
        prixValue = 'À contacter';
      } else {
        const parsed = parseFloat(this.prixInput.replace(',', '.'));
        if (!isNaN(parsed)) {
          prixValue = parsed;
        } else {
          prixValue = this.prixInput;
        }
      }
    }

    // Prepare service data
    const serviceData = {
      name: this.service.name || '',
      description: this.service.description || '',
      agencyName: this.service.agencyName || '',
      prix: prixValue,
      status: this.service.status || '',
      categorie: this.service.categorie || '',
      categoryList: this.service.categoryList || [],
      imageUrl: this.service.imageUrl || '',
      promoTitle: this.service.promoTitle || '',
      promoImg: this.service.promoImg || '',
      videoUrl: this.videoUrl || ''   // <-- NEW FIELD
    };

    console.log('Service data to update:', serviceData);

    if (!this.idService) {
      Swal.fire({
        icon: 'error',
        title: 'ID manquant',
        text: 'Impossible de mettre à jour le service sans ID',
        confirmButtonColor: '#f44336'
      });
      this.isSubmitting = false;
      return;
    }

    this.servicesService.updateService(this.idService, serviceData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: 'Service mis à jour avec succès',
          confirmButtonColor: '#4caf50',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.location.back();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la mise à jour du service';

    if (error.error?.message) {
      if (error.error.message.includes('Cannot invoke "String.toLowerCase()"')) {
        errorMessage = 'Veuillez vérifier les données du formulaire';
      } else {
        errorMessage = error.error.message;
      }
    } else if (error.error?.text) {
      errorMessage = error.error.text;
    }

    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: errorMessage,
      confirmButtonColor: '#f44336'
    });
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadServiceDetails();
        this.categoryInput = '';
        this.isMainImageChanged = false;
        this.isPromoImageChanged = false;
        // videoUrl will be reloaded via loadServiceDetails()
      }
    });
  }

  cancel(): void {
    this.router.navigate(['listeServices']);
  }
}